import React from 'react';
import { useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';

import type { Atalho } from '#components/finapp/ajustes/GrupoDeAtalhos';
import { GrupoDeAtalhos } from '#components/finapp/ajustes/GrupoDeAtalhos';
import { useIsTestEnv } from '#hooks/useIsTestEnv';
import { useSyncServerStatus } from '#hooks/useSyncServerStatus';

/**
 * Os atalhos do topo de Ajustes — o guarda-chuva das telas do Actual que
 * saíram do menu principal.
 *
 * ⭐ Elas saem do MENU, não do app: toda tela continua existindo e alcançável.
 * É isso que mantém o código do upstream intocado e o update mensal barato.
 *
 * ⚠️ Os nomes aqui não são tradução literal — são o vocabulário que o dono
 * usa. "Schedules" vira **Contas fixas** porque é onde ele cadastra aluguel,
 * Verisure e seguro; não é "agendamento", é a lista que alimenta os fixos do
 * mês. "Rules" vira **Categorização automática** porque "Regras" não diz
 * regras de quê.
 */
export function AtalhosAjustes() {
  const { t } = useTranslation();
  const syncServerStatus = useSyncServerStatus();
  const isTestEnv = useIsTestEnv();
  const temServidor = syncServerStatus !== 'no-server' || isTestEnv;

  const doDiaADia: Atalho[] = [
    {
      titulo: t('Contas fixas'),
      descricao: t('Aluguel, seguro, assinaturas — o que se repete todo mês.'),
      para: '/schedules',
    },
    {
      titulo: t('Categorização automática'),
      descricao: t('As regras que classificam seus lançamentos sozinhas.'),
      para: '/rules',
    },
    {
      titulo: t('Quem recebe'),
      descricao: t('Os nomes que aparecem nos seus lançamentos.'),
      para: '/payees',
    },
    ...(temServidor
      ? [
          {
            titulo: t('Meus bancos'),
            descricao: t('Conexões do Open Finance e reautorização.'),
            para: '/bank-sync',
          },
        ]
      : []),
    {
      titulo: t('Etiquetas'),
      descricao: t('As marcas que você usa nos lançamentos, como #empresa.'),
      para: '/tags',
    },
  ];

  const avancadas: Atalho[] = [
    {
      titulo: t('Pendências'),
      descricao: t('A fila de lançamentos que pedem sua atenção.'),
      para: '/pendencias',
    },
    {
      titulo: t('Orçamento por categoria'),
      descricao: t('Definir um teto de gasto para cada categoria, mês a mês.'),
      para: '/budget',
    },
    {
      titulo: t('Relatórios'),
      descricao: t('Gráficos detalhados de gasto, saldo e patrimônio.'),
      para: '/reports',
    },
  ];

  return (
    <View style={{ gap: 20 }} data-testid="atalhos-ajustes">
      <GrupoDeAtalhos titulo={t('Do dia a dia')} atalhos={doDiaADia} />
      <GrupoDeAtalhos titulo={t('Telas avançadas')} atalhos={avancadas} />
    </View>
  );
}
